import React, { useState, useEffect, useMemo } from "react";
import {
  MdAdd,
  MdEdit,
  MdDelete,
  MdRefresh,
  MdSearch,
  MdCheckCircle,
  MdOutlineVisibility,
  MdHealing,
  MdHistoryEdu,
  MdMedication,
  MdRemoveRedEye,
  MdFilterList,
  MdOutlineRestartAlt,
  MdFormatListBulleted,
  MdClose,
  MdCheck
} from "react-icons/md";

const API_BASE = "https://api.visiontrackcrm.com";
const LOCAL_STORAGE_KEY = "visiontrack_clinical_dropdowns_master";

const CATEGORY_TABS = [
  { id: "complaints", label: "Chief Complaints", icon: <MdOutlineVisibility />, desc: "Patient reported symptoms & primary consultation reasons" },
  { id: "history", label: "Clinical History (H/O)", icon: <MdHistoryEdu />, desc: "Medical, Ocular, Drug Allergy & Medication history groups" },
  { id: "diagnosis", label: "Diagnosis Master", icon: <MdHealing />, desc: "Refractive, Cataract, Glaucoma, Cornea & Retina classifications" },
  { id: "rx", label: "Rx / Medications", icon: <MdMedication />, desc: "Eye drops, ointments, suspensions & oral clinical prescriptions" },
  { id: "vision", label: "Visual Acuity (VA)", icon: <MdRemoveRedEye />, desc: "Standard distance & near visual acuity testing scales" },
  { id: "lens_types", label: "Lens Recommendations", icon: <MdFormatListBulleted />, desc: "Optical order lens coatings, indexes & design types" }
];

const DEFAULT_PRESET_ITEMS = [
  // Chief Complaints (43)
  { id: "comp_1", category: "complaints", subcategory: "General", item_value: "Blurred Vision", display_order: 1, is_active: true },
  { id: "comp_2", category: "complaints", subcategory: "General", item_value: "Decreased Vision", display_order: 2, is_active: true },
  { id: "comp_3", category: "complaints", subcategory: "General", item_value: "Sudden Loss of Vision", display_order: 3, is_active: true },
  { id: "comp_4", category: "complaints", subcategory: "General", item_value: "Gradual Loss of Vision", display_order: 4, is_active: true },
  { id: "comp_5", category: "complaints", subcategory: "General", item_value: "Near Vision Difficulty", display_order: 5, is_active: true },
  { id: "comp_6", category: "complaints", subcategory: "General", item_value: "Distance Vision Difficulty", display_order: 6, is_active: true },
  { id: "comp_7", category: "complaints", subcategory: "General", item_value: "Difficulty Reading", display_order: 7, is_active: true },
  { id: "comp_8", category: "complaints", subcategory: "General", item_value: "Double Vision", display_order: 8, is_active: true },
  { id: "comp_9", category: "complaints", subcategory: "General", item_value: "Distorted Vision", display_order: 9, is_active: true },
  { id: "comp_10", category: "complaints", subcategory: "General", item_value: "Poor Night Vision", display_order: 10, is_active: true },
  { id: "comp_11", category: "complaints", subcategory: "General", item_value: "Eye Pain", display_order: 11, is_active: true },
  { id: "comp_12", category: "complaints", subcategory: "General", item_value: "Headache", display_order: 12, is_active: true },
  { id: "comp_13", category: "complaints", subcategory: "General", item_value: "Eye Strain", display_order: 13, is_active: true },
  { id: "comp_14", category: "complaints", subcategory: "General", item_value: "Foreign Body Sensation", display_order: 14, is_active: true },
  { id: "comp_15", category: "complaints", subcategory: "General", item_value: "Burning Sensation", display_order: 15, is_active: true },
  { id: "comp_16", category: "complaints", subcategory: "General", item_value: "Itching", display_order: 16, is_active: true },
  { id: "comp_17", category: "complaints", subcategory: "General", item_value: "Irritation", display_order: 17, is_active: true },
  { id: "comp_18", category: "complaints", subcategory: "General", item_value: "Dryness", display_order: 18, is_active: true },
  { id: "comp_19", category: "complaints", subcategory: "General", item_value: "Eye Fatigue", display_order: 19, is_active: true },
  { id: "comp_20", category: "complaints", subcategory: "General", item_value: "Red Eye", display_order: 20, is_active: true },
  { id: "comp_21", category: "complaints", subcategory: "General", item_value: "Watering", display_order: 21, is_active: true },
  { id: "comp_22", category: "complaints", subcategory: "General", item_value: "Sticky Eyes", display_order: 22, is_active: true },
  { id: "comp_23", category: "complaints", subcategory: "General", item_value: "Eye Discharge", display_order: 23, is_active: true },
  { id: "comp_24", category: "complaints", subcategory: "General", item_value: "Swollen Eyelid", display_order: 24, is_active: true },
  { id: "comp_25", category: "complaints", subcategory: "General", item_value: "Photophobia", display_order: 25, is_active: true },
  { id: "comp_26", category: "complaints", subcategory: "General", item_value: "Glare", display_order: 26, is_active: true },
  { id: "comp_27", category: "complaints", subcategory: "General", item_value: "Halos Around Lights", display_order: 27, is_active: true },
  { id: "comp_28", category: "complaints", subcategory: "General", item_value: "Floaters", display_order: 28, is_active: true },
  { id: "comp_29", category: "complaints", subcategory: "General", item_value: "Flashes of Light", display_order: 29, is_active: true },
  { id: "comp_30", category: "complaints", subcategory: "General", item_value: "Eye Injury", display_order: 30, is_active: true },
  { id: "comp_31", category: "complaints", subcategory: "General", item_value: "Foreign Body in Eye", display_order: 31, is_active: true },
  { id: "comp_32", category: "complaints", subcategory: "General", item_value: "Chemical Injury", display_order: 32, is_active: true },
  { id: "comp_33", category: "complaints", subcategory: "General", item_value: "Blunt Trauma", display_order: 33, is_active: true },
  { id: "comp_34", category: "complaints", subcategory: "General", item_value: "Glasses Check-up", display_order: 34, is_active: true },
  { id: "comp_35", category: "complaints", subcategory: "General", item_value: "Power Change", display_order: 35, is_active: true },
  { id: "comp_36", category: "complaints", subcategory: "General", item_value: "Broken Glasses", display_order: 36, is_active: true },
  { id: "comp_37", category: "complaints", subcategory: "General", item_value: "Lost Glasses", display_order: 37, is_active: true },
  { id: "comp_38", category: "complaints", subcategory: "General", item_value: "Contact Lens Discomfort", display_order: 38, is_active: true },
  { id: "comp_39", category: "complaints", subcategory: "General", item_value: "Routine Eye Check-up", display_order: 39, is_active: true },
  { id: "comp_40", category: "complaints", subcategory: "General", item_value: "Follow-up Visit", display_order: 40, is_active: true },
  { id: "comp_41", category: "complaints", subcategory: "General", item_value: "School Eye Check-up", display_order: 41, is_active: true },
  { id: "comp_42", category: "complaints", subcategory: "General", item_value: "Pre-operative Check-up", display_order: 42, is_active: true },
  { id: "comp_43", category: "complaints", subcategory: "General", item_value: "Post-operative Review", display_order: 43, is_active: true },

  // Clinical History (23)
  { id: "hist_1", category: "history", subcategory: "Medical History", item_value: "DM (Diabetes Mellitus)", display_order: 1, is_active: true },
  { id: "hist_2", category: "history", subcategory: "Medical History", item_value: "HTN (Hypertension)", display_order: 2, is_active: true },
  { id: "hist_3", category: "history", subcategory: "Medical History", item_value: "DM + HTN", display_order: 3, is_active: true },
  { id: "hist_4", category: "history", subcategory: "Medical History", item_value: "Thyroid Disorder", display_order: 4, is_active: true },
  { id: "hist_5", category: "history", subcategory: "Medical History", item_value: "Heart Disease", display_order: 5, is_active: true },
  { id: "hist_6", category: "history", subcategory: "Medical History", item_value: "Asthma / Respiratory", display_order: 6, is_active: true },
  { id: "hist_7", category: "history", subcategory: "Medical History", item_value: "Others", display_order: 7, is_active: true },

  { id: "hist_8", category: "history", subcategory: "Ocular History", item_value: "Previous Cataract Surgery", display_order: 8, is_active: true },
  { id: "hist_9", category: "history", subcategory: "Ocular History", item_value: "Previous Eye Surgery", display_order: 9, is_active: true },
  { id: "hist_10", category: "history", subcategory: "Ocular History", item_value: "Previous Eye Injury", display_order: 10, is_active: true },
  { id: "hist_11", category: "history", subcategory: "Ocular History", item_value: "Glasses User", display_order: 11, is_active: true },
  { id: "hist_12", category: "history", subcategory: "Ocular History", item_value: "Contact Lens User", display_order: 12, is_active: true },
  { id: "hist_13", category: "history", subcategory: "Ocular History", item_value: "Others", display_order: 13, is_active: true },

  { id: "hist_14", category: "history", subcategory: "Drug / Allergy History", item_value: "Drug Allergy", display_order: 14, is_active: true },
  { id: "hist_15", category: "history", subcategory: "Drug / Allergy History", item_value: "Eye Drop Allergy", display_order: 15, is_active: true },
  { id: "hist_16", category: "history", subcategory: "Drug / Allergy History", item_value: "No Known Allergy", display_order: 16, is_active: true },
  { id: "hist_17", category: "history", subcategory: "Drug / Allergy History", item_value: "Others", display_order: 17, is_active: true },

  { id: "hist_18", category: "history", subcategory: "Current Medication", item_value: "Diabetes Medicine", display_order: 18, is_active: true },
  { id: "hist_19", category: "history", subcategory: "Current Medication", item_value: "BP Medicine", display_order: 19, is_active: true },
  { id: "hist_20", category: "history", subcategory: "Current Medication", item_value: "Eye Drops", display_order: 20, is_active: true },
  { id: "hist_21", category: "history", subcategory: "Current Medication", item_value: "Steroids", display_order: 21, is_active: true },
  { id: "hist_22", category: "history", subcategory: "Current Medication", item_value: "Others", display_order: 22, is_active: true },

  // Diagnosis Master (33)
  { id: "diag_1", category: "diagnosis", subcategory: "Refractive", item_value: "Myopia", display_order: 1, is_active: true },
  { id: "diag_2", category: "diagnosis", subcategory: "Refractive", item_value: "Hyperopia", display_order: 2, is_active: true },
  { id: "diag_3", category: "diagnosis", subcategory: "Refractive", item_value: "Astigmatism", display_order: 3, is_active: true },
  { id: "diag_4", category: "diagnosis", subcategory: "Refractive", item_value: "Presbyopia", display_order: 4, is_active: true },
  { id: "diag_5", category: "diagnosis", subcategory: "Refractive", item_value: "Amblyopia", display_order: 5, is_active: true },

  { id: "diag_6", category: "diagnosis", subcategory: "Lens", item_value: "Immature Cataract (IMC)", display_order: 6, is_active: true },
  { id: "diag_7", category: "diagnosis", subcategory: "Lens", item_value: "Mature Cataract (MC)", display_order: 7, is_active: true },
  { id: "diag_8", category: "diagnosis", subcategory: "Lens", item_value: "Pseudophakia (PCIOL)", display_order: 8, is_active: true },
  { id: "diag_9", category: "diagnosis", subcategory: "Lens", item_value: "Aphakia", display_order: 9, is_active: true },

  { id: "diag_10", category: "diagnosis", subcategory: "Conjunctiva", item_value: "Acute Conjunctivitis", display_order: 10, is_active: true },
  { id: "diag_11", category: "diagnosis", subcategory: "Conjunctiva", item_value: "Allergic Conjunctivitis", display_order: 11, is_active: true },
  { id: "diag_12", category: "diagnosis", subcategory: "Conjunctiva", item_value: "Pterygium (PTG)", display_order: 12, is_active: true },

  { id: "diag_13", category: "diagnosis", subcategory: "Cornea", item_value: "Corneal Abrasion", display_order: 13, is_active: true },
  { id: "diag_14", category: "diagnosis", subcategory: "Cornea", item_value: "Corneal Ulcer", display_order: 14, is_active: true },
  { id: "diag_15", category: "diagnosis", subcategory: "Cornea", item_value: "Keratitis", display_order: 15, is_active: true },
  { id: "diag_16", category: "diagnosis", subcategory: "Cornea", item_value: "Dry Eye Disease (DED)", display_order: 16, is_active: true },

  { id: "diag_17", category: "diagnosis", subcategory: "Eyelid", item_value: "Blepharitis", display_order: 17, is_active: true },
  { id: "diag_18", category: "diagnosis", subcategory: "Eyelid", item_value: "Hordeolum", display_order: 18, is_active: true },
  { id: "diag_19", category: "diagnosis", subcategory: "Eyelid", item_value: "Chalazion", display_order: 19, is_active: true },
  { id: "diag_20", category: "diagnosis", subcategory: "Eyelid", item_value: "Ptosis", display_order: 20, is_active: true },

  { id: "diag_21", category: "diagnosis", subcategory: "Glaucoma", item_value: "POAG (Primary Open Angle)", display_order: 21, is_active: true },
  { id: "diag_22", category: "diagnosis", subcategory: "Glaucoma", item_value: "PACG (Primary Angle Closure)", display_order: 22, is_active: true },
  { id: "diag_23", category: "diagnosis", subcategory: "Glaucoma", item_value: "Ocular Hypertension", display_order: 23, is_active: true },

  { id: "diag_24", category: "diagnosis", subcategory: "Retina", item_value: "Diabetic Retinopathy (DR)", display_order: 24, is_active: true },
  { id: "diag_25", category: "diagnosis", subcategory: "Retina", item_value: "Hypertensive Retinopathy", display_order: 25, is_active: true },
  { id: "diag_26", category: "diagnosis", subcategory: "Retina", item_value: "AMD (Age-Related Macular)", display_order: 26, is_active: true },
  { id: "diag_27", category: "diagnosis", subcategory: "Retina", item_value: "Retinal Detachment (RD)", display_order: 27, is_active: true },

  { id: "diag_28", category: "diagnosis", subcategory: "Others", item_value: "Normal Eye Examination", display_order: 28, is_active: true },
  { id: "diag_29", category: "diagnosis", subcategory: "Others", item_value: "Post Operative Review", display_order: 29, is_active: true },
  { id: "diag_30", category: "diagnosis", subcategory: "Others", item_value: "Follow-up Case", display_order: 30, is_active: true },

  // Rx & Medications (18)
  { id: "rx_1", category: "rx", subcategory: "Medications", item_value: "Carboxymethyl Cellulose (CMC) 0.5%", display_order: 1, is_active: true },
  { id: "rx_2", category: "rx", subcategory: "Medications", item_value: "Sodium Hyaluronate 0.1%", display_order: 2, is_active: true },
  { id: "rx_3", category: "rx", subcategory: "Medications", item_value: "Moxifloxacin 0.5%", display_order: 3, is_active: true },
  { id: "rx_4", category: "rx", subcategory: "Medications", item_value: "Gatifloxacin 0.3%", display_order: 4, is_active: true },
  { id: "rx_5", category: "rx", subcategory: "Medications", item_value: "Ofloxacin 0.3%", display_order: 5, is_active: true },
  { id: "rx_6", category: "rx", subcategory: "Medications", item_value: "Tobramycin 0.3%", display_order: 6, is_active: true },
  { id: "rx_7", category: "rx", subcategory: "Medications", item_value: "Moxifloxacin + Dexamethasone", display_order: 7, is_active: true },
  { id: "rx_8", category: "rx", subcategory: "Medications", item_value: "Prednisolone Acetate 1%", display_order: 8, is_active: true },
  { id: "rx_9", category: "rx", subcategory: "Medications", item_value: "Loteprednol Etabonate 0.5%", display_order: 9, is_active: true },
  { id: "rx_10", category: "rx", subcategory: "Medications", item_value: "Olopatadine 0.1%", display_order: 10, is_active: true },
  { id: "rx_11", category: "rx", subcategory: "Medications", item_value: "Timolol Maleate 0.5%", display_order: 11, is_active: true },
  { id: "rx_12", category: "rx", subcategory: "Medications", item_value: "Brimonidine Tartrate 0.2%", display_order: 12, is_active: true },
  { id: "rx_13", category: "rx", subcategory: "Medications", item_value: "Latanoprost 0.005%", display_order: 13, is_active: true },
  { id: "rx_14", category: "rx", subcategory: "Medications", item_value: "Tropicamide 1%", display_order: 14, is_active: true },
  { id: "rx_15", category: "rx", subcategory: "Medications", item_value: "Cyclopentolate 1%", display_order: 15, is_active: true },
  { id: "rx_16", category: "rx", subcategory: "Medications", item_value: "Homaptropine 2%", display_order: 16, is_active: true },
  { id: "rx_17", category: "rx", subcategory: "Medications", item_value: "Nepafenac 0.1%", display_order: 17, is_active: true },
  { id: "rx_18", category: "rx", subcategory: "Medications", item_value: "Ketorolac Tromethamine 0.5%", display_order: 18, is_active: true },

  // Visual Acuity VA (18)
  { id: "va_1", category: "vision", subcategory: "Scales", item_value: "6/6", display_order: 1, is_active: true },
  { id: "va_2", category: "vision", subcategory: "Scales", item_value: "6/9", display_order: 2, is_active: true },
  { id: "va_3", category: "vision", subcategory: "Scales", item_value: "6/12", display_order: 3, is_active: true },
  { id: "va_4", category: "vision", subcategory: "Scales", item_value: "6/18", display_order: 4, is_active: true },
  { id: "va_5", category: "vision", subcategory: "Scales", item_value: "6/24", display_order: 5, is_active: true },
  { id: "va_6", category: "vision", subcategory: "Scales", item_value: "6/36", display_order: 6, is_active: true },
  { id: "va_7", category: "vision", subcategory: "Scales", item_value: "6/60", display_order: 7, is_active: true },
  { id: "va_8", category: "vision", subcategory: "Scales", item_value: "5/60", display_order: 8, is_active: true },
  { id: "va_9", category: "vision", subcategory: "Scales", item_value: "4/60", display_order: 9, is_active: true },
  { id: "va_10", category: "vision", subcategory: "Scales", item_value: "3/60", display_order: 10, is_active: true },
  { id: "va_11", category: "vision", subcategory: "Scales", item_value: "2/60", display_order: 11, is_active: true },
  { id: "va_12", category: "vision", subcategory: "Scales", item_value: "1/60", display_order: 12, is_active: true },
  { id: "va_13", category: "vision", subcategory: "Scales", item_value: "CF 1", display_order: 13, is_active: true },
  { id: "va_14", category: "vision", subcategory: "Scales", item_value: "CF 2", display_order: 14, is_active: true },
  { id: "va_15", category: "vision", subcategory: "Scales", item_value: "CF 3", display_order: 15, is_active: true },
  { id: "va_16", category: "vision", subcategory: "Scales", item_value: "HM (Hand Movements)", display_order: 16, is_active: true },
  { id: "va_17", category: "vision", subcategory: "Scales", item_value: "PL + PR", display_order: 17, is_active: true },
  { id: "va_18", category: "vision", subcategory: "Scales", item_value: "NPL (No Perception of Light)", display_order: 18, is_active: true },

  // Lens Recommendations (12)
  { id: "lens_1", category: "lens_types", subcategory: "Recommendations", item_value: "Single Vision", display_order: 1, is_active: true },
  { id: "lens_2", category: "lens_types", subcategory: "Recommendations", item_value: "Bifocal (Kryptok / D-Bifocal)", display_order: 2, is_active: true },
  { id: "lens_3", category: "lens_types", subcategory: "Recommendations", item_value: "Progressive (Standard)", display_order: 3, is_active: true },
  { id: "lens_4", category: "lens_types", subcategory: "Recommendations", item_value: "Progressive (Digital Freeform)", display_order: 4, is_active: true },
  { id: "lens_5", category: "lens_types", subcategory: "Recommendations", item_value: "Blue-Cut / Blue-Block", display_order: 5, is_active: true },
  { id: "lens_6", category: "lens_types", subcategory: "Recommendations", item_value: "Anti-Reflective Coating (ARC)", display_order: 6, is_active: true },
  { id: "lens_7", category: "lens_types", subcategory: "Recommendations", item_value: "Photochromic / Transition", display_order: 7, is_active: true },
  { id: "lens_8", category: "lens_types", subcategory: "Recommendations", item_value: "High Index 1.56", display_order: 8, is_active: true },
  { id: "lens_9", category: "lens_types", subcategory: "Recommendations", item_value: "High Index 1.61 / 1.67", display_order: 9, is_active: true },
  { id: "lens_10", category: "lens_types", subcategory: "Recommendations", item_value: "Polycarbonate / Impact Resistant", display_order: 10, is_active: true },
  { id: "lens_11", category: "lens_types", subcategory: "Recommendations", item_value: "Polarized", display_order: 11, is_active: true },
  { id: "lens_12", category: "lens_types", subcategory: "Recommendations", item_value: "Drivewear / Polar-Shield", display_order: 12, is_active: true }
];

export default function ClinicalDropdowns() {
  const [activeTab, setActiveTab] = useState("complaints");
  const [items, setItems] = useState(() => {
    try {
      const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (e) {}
    return DEFAULT_PRESET_ITEMS;
  });

  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSubcatFilter, setSelectedSubcatFilter] = useState("ALL");

  // Add / Edit Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [modalForm, setModalForm] = useState({
    category: "complaints",
    subcategory: "General",
    item_value: "",
    display_order: 0,
    is_active: true
  });
  const [customSubcatInput, setCustomSubcatInput] = useState("");

  // Feedback toast
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "success" }), 3500);
  };

  useEffect(() => {
    fetchDropdowns();
  }, []);

  // Save to localStorage whenever items change
  const saveItemsLocally = (updatedList) => {
    setItems(updatedList);
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updatedList));
    } catch (e) {}
  };

  const fetchDropdowns = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/clinicaldropdowns/all`);
      const result = await res.json();
      if (result?.success && Array.isArray(result.data) && result.data.length > 0) {
        saveItemsLocally(result.data);
      } else {
        // Fallback: If DB returned empty, use stored or default presets
        const currentStored = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (!currentStored) {
          saveItemsLocally(DEFAULT_PRESET_ITEMS);
        }
      }
    } catch (err) {
      console.log("Fetch dropdowns from DB fallback to presets:", err);
      const currentStored = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (!currentStored) {
        saveItemsLocally(DEFAULT_PRESET_ITEMS);
      }
    } finally {
      setLoading(false);
    }
  };

  // Filter items for the currently active tab
  const currentCategoryItems = useMemo(() => {
    return items.filter((it) => it.category === activeTab);
  }, [items, activeTab]);

  // Unique subcategories for current tab
  const subcategories = useMemo(() => {
    const set = new Set();
    currentCategoryItems.forEach((it) => {
      if (it.subcategory) set.add(it.subcategory);
    });
    return Array.from(set);
  }, [currentCategoryItems]);

  // Filtered items by search & subcategory filter
  const filteredItems = useMemo(() => {
    return currentCategoryItems.filter((it) => {
      const matchesSearch =
        searchQuery.trim() === "" ||
        String(it.item_value || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(it.subcategory || "").toLowerCase().includes(searchQuery.toLowerCase());

      const matchesSubcat =
        selectedSubcatFilter === "ALL" || it.subcategory === selectedSubcatFilter;

      return matchesSearch && matchesSubcat;
    });
  }, [currentCategoryItems, searchQuery, selectedSubcatFilter]);

  // Open Modal for Add
  const handleOpenAdd = (defaultSubcat = "") => {
    setEditingItem(null);
    setCustomSubcatInput("");
    setModalForm({
      category: activeTab,
      subcategory: defaultSubcat || (subcategories[0] || "General"),
      item_value: "",
      display_order: currentCategoryItems.length + 1,
      is_active: true
    });
    setShowModal(true);
  };

  // Open Modal for Edit
  const handleOpenEdit = (item) => {
    setEditingItem(item);
    setCustomSubcatInput("");
    setModalForm({
      category: item.category,
      subcategory: item.subcategory || "General",
      item_value: item.item_value,
      display_order: item.display_order || 0,
      is_active: item.is_active !== false
    });
    setShowModal(true);
  };

  // Save Item (Create or Update)
  const handleSave = async (e) => {
    e?.preventDefault();
    const finalSubcat =
      modalForm.subcategory === "__NEW__"
        ? customSubcatInput.trim() || "General"
        : modalForm.subcategory;

    if (!modalForm.item_value.trim()) {
      showToast("Please enter an item name/value.", "error");
      return;
    }

    const cleanVal = modalForm.item_value.trim();

    try {
      setLoading(true);
      if (editingItem) {
        // Update in state & storage
        const updatedList = items.map((it) =>
          it.id === editingItem.id
            ? {
                ...it,
                item_value: cleanVal,
                subcategory: finalSubcat,
                is_active: modalForm.is_active,
                display_order: modalForm.display_order
              }
            : it
        );
        saveItemsLocally(updatedList);
        showToast(`Updated '${cleanVal}' successfully!`);
        setShowModal(false);

        // Async sync to DB
        try {
          await fetch(`${API_BASE}/clinicaldropdowns/update/${editingItem.id}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              item_value: cleanVal,
              subcategory: finalSubcat,
              is_active: modalForm.is_active,
              display_order: modalForm.display_order
            })
          });
        } catch (e) {}
      } else {
        // Add new
        const newItem = {
          id: `custom_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
          category: activeTab,
          subcategory: finalSubcat,
          item_value: cleanVal,
          display_order: modalForm.display_order,
          is_active: true
        };
        const updatedList = [newItem, ...items];
        saveItemsLocally(updatedList);
        showToast(`Added '${cleanVal}' to ${activeTab}!`);
        setShowModal(false);

        // Async sync to DB
        try {
          await fetch(`${API_BASE}/clinicaldropdowns/add`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              category: activeTab,
              subcategory: finalSubcat,
              item_value: cleanVal,
              display_order: modalForm.display_order
            })
          });
        } catch (e) {}
      }
    } catch (err) {
      console.error("Save item error:", err);
      showToast("Error saving item.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Delete Item (Super Admin only)
  const handleDelete = async (item) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${item.item_value}" from the master dropdown list?`
    );
    if (!confirmDelete) return;

    try {
      const updatedList = items.filter((it) => it.id !== item.id);
      saveItemsLocally(updatedList);
      showToast(`Deleted "${item.item_value}" successfully.`);

      // Async sync to DB
      try {
        await fetch(`${API_BASE}/clinicaldropdowns/delete/${item.id}`, {
          method: "DELETE"
        });
      } catch (e) {}
    } catch (err) {
      console.error("Delete error:", err);
      showToast("Error deleting item.", "error");
    }
  };

  // Toggle Active / Inactive
  const handleToggleActive = async (item) => {
    try {
      const nextActive = !item.is_active;
      const updatedList = items.map((it) =>
        it.id === item.id ? { ...it, is_active: nextActive } : it
      );
      saveItemsLocally(updatedList);
      showToast(`Item ${nextActive ? "enabled" : "disabled"}.`);

      // Async sync to DB
      try {
        await fetch(`${API_BASE}/clinicaldropdowns/update/${item.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ is_active: nextActive })
        });
      } catch (e) {}
    } catch (err) {
      console.error("Toggle error:", err);
    }
  };

  // Reset to Factory System Defaults
  const handleResetDefaults = async () => {
    const confirmed = window.confirm(
      "⚠️ WARNING: This will reset all clinical dropdown lists (Complaints, History, Diagnosis, Rx, Vision) back to standard system defaults. Are you sure you want to proceed?"
    );
    if (!confirmed) return;

    try {
      setLoading(true);
      saveItemsLocally(DEFAULT_PRESET_ITEMS);
      showToast("Dropdown master lists reset to system defaults successfully!");

      try {
        await fetch(`${API_BASE}/clinicaldropdowns/reset-defaults`, {
          method: "POST"
        });
      } catch (e) {}
    } catch (err) {
      console.error("Reset defaults error:", err);
      showToast("Error during reset.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Counts summary
  const totalComplaints = items.filter((i) => i.category === "complaints").length;
  const totalHistory = items.filter((i) => i.category === "history").length;
  const totalDiagnosis = items.filter((i) => i.category === "diagnosis").length;
  const totalRx = items.filter((i) => i.category === "rx").length;

  const currentTabObj = CATEGORY_TABS.find((t) => t.id === activeTab) || CATEGORY_TABS[0];

  return (
    <div className="cd-page-container">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`cd-toast cd-toast-${toast.type}`}>
          {toast.type === "success" ? <MdCheckCircle size={18} /> : <MdClose size={18} />}
          <span>{toast.message}</span>
        </div>
      )}

      {/* Header Banner */}
      <div className="cd-header-card">
        <div className="cd-header-left">
          <div className="cd-header-icon-box">
            <MdFormatListBulleted size={28} color="#0284C7" />
          </div>
          <div>
            <h1 className="cd-page-title">Eye Exam & Clinical Master Dropdowns</h1>
            <p className="cd-page-subtitle">
              Configure and maintain global dropdown presets for Chief Complaints, Clinical History, Diagnoses, Medications & Lenses across all store branches.
            </p>
          </div>
        </div>

        <div className="cd-header-actions">
          <button className="cd-btn cd-btn-secondary" onClick={fetchDropdowns} disabled={loading}>
            <MdRefresh size={18} className={loading ? "spin" : ""} />
            <span>Refresh</span>
          </button>

          <button className="cd-btn cd-btn-danger-outline" onClick={handleResetDefaults} title="Restore standard default master presets">
            <MdOutlineRestartAlt size={18} />
            <span>Reset Defaults</span>
          </button>

          <button className="cd-btn cd-btn-primary" onClick={() => handleOpenAdd()}>
            <MdAdd size={20} />
            <span>Add New Preset</span>
          </button>
        </div>
      </div>

      {/* KPI Stats Overview */}
      <div className="cd-stats-grid">
        <div className="cd-stat-card" onClick={() => setActiveTab("complaints")}>
          <div className="cd-stat-icon-wrap" style={{ background: "#EFF6FF", color: "#2563EB" }}>
            <MdOutlineVisibility size={22} />
          </div>
          <div className="cd-stat-info">
            <span className="cd-stat-label">Chief Complaints</span>
            <span className="cd-stat-value">{totalComplaints} Presets</span>
          </div>
        </div>

        <div className="cd-stat-card" onClick={() => setActiveTab("history")}>
          <div className="cd-stat-icon-wrap" style={{ background: "#F0FDF4", color: "#16A34A" }}>
            <MdHistoryEdu size={22} />
          </div>
          <div className="cd-stat-info">
            <span className="cd-stat-label">Clinical History Items</span>
            <span className="cd-stat-value">{totalHistory} Presets</span>
          </div>
        </div>

        <div className="cd-stat-card" onClick={() => setActiveTab("diagnosis")}>
          <div className="cd-stat-icon-wrap" style={{ background: "#FAF5FF", color: "#9333EA" }}>
            <MdHealing size={22} />
          </div>
          <div className="cd-stat-info">
            <span className="cd-stat-label">Diagnosis Master Items</span>
            <span className="cd-stat-value">{totalDiagnosis} Conditions</span>
          </div>
        </div>

        <div className="cd-stat-card" onClick={() => setActiveTab("rx")}>
          <div className="cd-stat-icon-wrap" style={{ background: "#FFF7ED", color: "#EA580C" }}>
            <MdMedication size={22} />
          </div>
          <div className="cd-stat-info">
            <span className="cd-stat-label">Rx & Medications</span>
            <span className="cd-stat-value">{totalRx} Drugs</span>
          </div>
        </div>
      </div>

      {/* Main Content Card with Navigation Tabs */}
      <div className="cd-main-card">
        {/* Navigation Tabs Bar */}
        <div className="cd-tabs-bar">
          {CATEGORY_TABS.map((tab) => {
            const count = items.filter((i) => i.category === tab.id).length;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                className={`cd-tab-btn ${isActive ? "cd-tab-btn-active" : ""}`}
                onClick={() => {
                  setActiveTab(tab.id);
                  setSelectedSubcatFilter("ALL");
                  setSearchQuery("");
                }}
              >
                <span className="cd-tab-icon">{tab.icon}</span>
                <span className="cd-tab-text">{tab.label}</span>
                <span className={`cd-tab-badge ${isActive ? "cd-tab-badge-active" : ""}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Tab Context Banner */}
        <div className="cd-tab-banner">
          <div>
            <h3 className="cd-tab-title">{currentTabObj.label}</h3>
            <p className="cd-tab-desc">{currentTabObj.desc}</p>
          </div>

          <div className="cd-tab-controls">
            {/* Subcategory Filter if tab has multiple groups */}
            {subcategories.length > 1 && (
              <div className="cd-subcat-filter">
                <MdFilterList size={16} color="#64748B" />
                <select
                  value={selectedSubcatFilter}
                  onChange={(e) => setSelectedSubcatFilter(e.target.value)}
                  className="cd-select-input"
                >
                  <option value="ALL">All Groups ({currentCategoryItems.length})</option>
                  {subcategories.map((sub) => {
                    const subCount = currentCategoryItems.filter((i) => i.subcategory === sub).length;
                    return (
                      <option key={sub} value={sub}>
                        {sub} ({subCount})
                      </option>
                    );
                  })}
                </select>
              </div>
            )}

            {/* Search Input */}
            <div className="cd-search-box">
              <MdSearch size={18} color="#94A3B8" />
              <input
                type="text"
                placeholder={`Search ${currentTabObj.label}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="cd-search-input"
              />
              {searchQuery && (
                <button className="cd-clear-btn" onClick={() => setSearchQuery("")}>
                  <MdClose size={14} />
                </button>
              )}
            </div>

            <button className="cd-btn cd-btn-primary cd-btn-sm" onClick={() => handleOpenAdd(selectedSubcatFilter !== "ALL" ? selectedSubcatFilter : "")}>
              <MdAdd size={16} />
              <span>Add to {currentTabObj.label}</span>
            </button>
          </div>
        </div>

        {/* Items Table */}
        <div className="cd-table-container">
          {loading && items.length === 0 ? (
            <div className="cd-loading-state">
              <div className="cd-spinner"></div>
              <p>Loading dropdown master lists...</p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="cd-empty-state">
              <MdFormatListBulleted size={48} color="#CBD5E1" />
              <h4>No Preset Items Found</h4>
              <p>
                {searchQuery
                  ? `No items matched your search "${searchQuery}".`
                  : `No entries created yet for ${currentTabObj.label}.`}
              </p>
              <button className="cd-btn cd-btn-primary cd-btn-sm" onClick={() => handleOpenAdd()}>
                <MdAdd size={16} />
                <span>Add First Item</span>
              </button>
            </div>
          ) : (
            <table className="cd-table">
              <thead>
                <tr>
                  <th style={{ width: "60px" }}>#</th>
                  <th>Item Name / Preset Value</th>
                  {subcategories.length > 0 && <th style={{ width: "220px" }}>Group / Category</th>}
                  <th style={{ width: "120px", textAlign: "center" }}>Status</th>
                  <th style={{ width: "140px", textAlign: "center" }}>Actions (Super Admin)</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item, index) => (
                  <tr key={item.id || index} className={!item.is_active ? "cd-row-inactive" : ""}>
                    <td className="cd-td-index">{index + 1}</td>
                    <td className="cd-td-name">
                      <div className="cd-item-name-cell">
                        <span className="cd-item-name-text">{item.item_value}</span>
                        {!item.is_active && <span className="cd-badge-disabled">Disabled</span>}
                      </div>
                    </td>
                    {subcategories.length > 0 && (
                      <td>
                        <span className="cd-group-badge">{item.subcategory || "General"}</span>
                      </td>
                    )}
                    <td style={{ textAlign: "center" }}>
                      <button
                        className={`cd-status-toggle ${item.is_active ? "cd-status-active" : "cd-status-inactive"}`}
                        onClick={() => handleToggleActive(item)}
                        title={item.is_active ? "Click to disable" : "Click to enable"}
                      >
                        {item.is_active ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td>
                      <div className="cd-actions-cell">
                        <button
                          className="cd-icon-btn cd-edit-btn"
                          onClick={() => handleOpenEdit(item)}
                          title="Edit preset value"
                        >
                          <MdEdit size={16} />
                        </button>
                        <button
                          className="cd-icon-btn cd-delete-btn"
                          onClick={() => handleDelete(item)}
                          title="Delete preset (Super Admin Only)"
                        >
                          <MdDelete size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Add / Edit Modal */}
      {showModal && (
        <div className="cd-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="cd-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="cd-modal-header">
              <div className="cd-modal-header-text">
                <h3>{editingItem ? "Edit Dropdown Preset" : `Add New to ${currentTabObj.label}`}</h3>
                <p>Configure value and category grouping for clinical dropdowns</p>
              </div>
              <button className="cd-modal-close-btn" onClick={() => setShowModal(false)}>
                <MdClose size={20} />
              </button>
            </div>

            <form onSubmit={handleSave} className="cd-modal-body">
              {/* Category / Subcategory Selection */}
              {subcategories.length > 0 && (
                <div className="cd-form-group">
                  <label className="cd-form-label">Classification / Group</label>
                  <select
                    value={modalForm.subcategory}
                    onChange={(e) => setModalForm({ ...modalForm, subcategory: e.target.value })}
                    className="cd-form-input"
                  >
                    {subcategories.map((sub) => (
                      <option key={sub} value={sub}>
                        {sub}
                      </option>
                    ))}
                    <option value="__NEW__">+ Add New Group Category...</option>
                  </select>
                </div>
              )}

              {modalForm.subcategory === "__NEW__" && (
                <div className="cd-form-group">
                  <label className="cd-form-label">New Group Category Name</label>
                  <input
                    type="text"
                    placeholder="e.g. Corneal Dystrophy, Rare Conditions"
                    value={customSubcatInput}
                    onChange={(e) => setCustomSubcatInput(e.target.value)}
                    className="cd-form-input"
                    required
                  />
                </div>
              )}

              {/* Item Value */}
              <div className="cd-form-group">
                <label className="cd-form-label">
                  Preset Item Value / Display Name <span className="cd-req">*</span>
                </label>
                <input
                  type="text"
                  placeholder={`Enter ${currentTabObj.label.toLowerCase()} name...`}
                  value={modalForm.item_value}
                  onChange={(e) => setModalForm({ ...modalForm, item_value: e.target.value })}
                  className="cd-form-input"
                  autoFocus
                  required
                />
              </div>

              {/* Active Toggle & Order */}
              <div className="cd-form-row">
                <div className="cd-form-group" style={{ flex: 1 }}>
                  <label className="cd-form-label">Display Order</label>
                  <input
                    type="number"
                    value={modalForm.display_order}
                    onChange={(e) => setModalForm({ ...modalForm, display_order: Number(e.target.value) })}
                    className="cd-form-input"
                  />
                </div>

                <div className="cd-form-group cd-checkbox-group">
                  <label className="cd-checkbox-label">
                    <input
                      type="checkbox"
                      checked={modalForm.is_active}
                      onChange={(e) => setModalForm({ ...modalForm, is_active: e.target.checked })}
                    />
                    <span>Active in Presets</span>
                  </label>
                </div>
              </div>

              <div className="cd-modal-footer">
                <button
                  type="button"
                  className="cd-btn cd-btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="cd-btn cd-btn-primary" disabled={loading}>
                  <MdCheck size={18} />
                  <span>{editingItem ? "Save Changes" : "Create Item"}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Scoped CSS Styles */}
      <style>{`
        .cd-page-container {
          padding: 24px;
          max-width: 1400px;
          margin: 0 auto;
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
          color: #0F172A;
        }

        /* HEADER CARD */
        .cd-header-card {
          background: #FFFFFF;
          border-radius: 16px;
          border: 1px solid #E2E8F0;
          padding: 24px 28px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 20px;
          box-shadow: 0 4px 20px -2px rgba(0, 0, 0, 0.05);
          margin-bottom: 24px;
        }

        .cd-header-left {
          display: flex;
          align-items: center;
          gap: 16px;
          flex: 1;
          min-width: 300px;
        }

        .cd-header-icon-box {
          width: 54px;
          height: 54px;
          border-radius: 14px;
          background: #EFF6FF;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .cd-page-title {
          font-size: 22px;
          font-weight: 800;
          color: #0F172A;
          margin: 0 0 4px 0;
          letter-spacing: -0.02em;
        }

        .cd-page-subtitle {
          font-size: 13px;
          color: #64748B;
          margin: 0;
          line-height: 1.5;
        }

        .cd-header-actions {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }

        /* STATS GRID */
        .cd-stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 16px;
          margin-bottom: 24px;
        }

        .cd-stat-card {
          background: #FFFFFF;
          border: 1px solid #E2E8F0;
          border-radius: 14px;
          padding: 16px 20px;
          display: flex;
          align-items: center;
          gap: 14px;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 2px 8px rgba(0,0,0,0.02);
        }

        .cd-stat-card:hover {
          transform: translateY(-2px);
          border-color: #0284C7;
          box-shadow: 0 6px 16px rgba(2, 132, 199, 0.08);
        }

        .cd-stat-icon-wrap {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .cd-stat-info {
          display: flex;
          flex-direction: column;
        }

        .cd-stat-label {
          font-size: 11.5px;
          font-weight: 600;
          color: #64748B;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }

        .cd-stat-value {
          font-size: 17px;
          font-weight: 800;
          color: #0F172A;
          margin-top: 2px;
        }

        /* MAIN CARD & TABS */
        .cd-main-card {
          background: #FFFFFF;
          border-radius: 16px;
          border: 1px solid #E2E8F0;
          box-shadow: 0 4px 20px -2px rgba(0, 0, 0, 0.05);
          overflow: hidden;
        }

        .cd-tabs-bar {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 12px 16px;
          background: #F8FAFC;
          border-bottom: 1px solid #E2E8F0;
          overflow-x: auto;
        }

        .cd-tab-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 18px;
          border-radius: 10px;
          background: transparent;
          border: none;
          font-size: 13px;
          font-weight: 600;
          color: #64748B;
          cursor: pointer;
          transition: all 0.2s;
          white-space: nowrap;
        }

        .cd-tab-btn:hover {
          background: #E2E8F0;
          color: #0F172A;
        }

        .cd-tab-btn-active {
          background: #0284C7 !important;
          color: #FFFFFF !important;
          box-shadow: 0 4px 12px rgba(2, 132, 199, 0.25);
        }

        .cd-tab-badge {
          background: #E2E8F0;
          color: #475569;
          font-size: 11px;
          font-weight: 700;
          padding: 2px 8px;
          border-radius: 12px;
        }

        .cd-tab-badge-active {
          background: rgba(255, 255, 255, 0.25);
          color: #FFFFFF;
        }

        /* TAB BANNER & CONTROLS */
        .cd-tab-banner {
          padding: 18px 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 16px;
          border-bottom: 1px solid #F1F5F9;
          background: #FFFFFF;
        }

        .cd-tab-title {
          font-size: 16px;
          font-weight: 800;
          color: #0F172A;
          margin: 0 0 2px 0;
        }

        .cd-tab-desc {
          font-size: 12px;
          color: #64748B;
          margin: 0;
        }

        .cd-tab-controls {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }

        .cd-subcat-filter {
          display: flex;
          align-items: center;
          gap: 6px;
          background: #F8FAFC;
          border: 1px solid #CBD5E1;
          border-radius: 8px;
          padding: 6px 12px;
        }

        .cd-select-input {
          border: none;
          background: transparent;
          font-size: 12.5px;
          font-weight: 600;
          color: #334155;
          outline: none;
          cursor: pointer;
        }

        .cd-search-box {
          display: flex;
          align-items: center;
          gap: 8px;
          background: #F8FAFC;
          border: 1px solid #CBD5E1;
          border-radius: 8px;
          padding: 6px 12px;
          min-width: 220px;
        }

        .cd-search-input {
          border: none;
          background: transparent;
          font-size: 12.5px;
          color: #0F172A;
          outline: none;
          width: 100%;
        }

        .cd-clear-btn {
          background: transparent;
          border: none;
          color: #94A3B8;
          cursor: pointer;
          display: flex;
          padding: 0;
        }

        /* TABLE */
        .cd-table-container {
          overflow-x: auto;
          min-height: 280px;
        }

        .cd-table {
          width: 100%;
          border-collapse: collapse;
          text-align: left;
          font-size: 13px;
        }

        .cd-table th {
          background: #F8FAFC;
          padding: 12px 20px;
          font-size: 11px;
          font-weight: 700;
          color: #475569;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 1px solid #E2E8F0;
        }

        .cd-table td {
          padding: 14px 20px;
          border-bottom: 1px solid #F1F5F9;
          color: #1E293B;
          vertical-align: middle;
        }

        .cd-table tbody tr:hover {
          background: #F8FAFC;
        }

        .cd-row-inactive {
          opacity: 0.6;
          background: #FAFAFA;
        }

        .cd-td-index {
          color: #94A3B8;
          font-weight: 600;
        }

        .cd-item-name-cell {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .cd-item-name-text {
          font-weight: 700;
          color: #0F172A;
          font-size: 13.5px;
        }

        .cd-badge-disabled {
          font-size: 10px;
          font-weight: 700;
          background: #FEE2E2;
          color: #DC2626;
          padding: 2px 6px;
          border-radius: 4px;
          text-transform: uppercase;
        }

        .cd-group-badge {
          background: #EFF6FF;
          color: #0284C7;
          border: 1px solid #BAE6FD;
          font-size: 11.5px;
          font-weight: 600;
          padding: 4px 10px;
          border-radius: 20px;
          display: inline-block;
        }

        .cd-status-toggle {
          border: none;
          padding: 4px 12px;
          border-radius: 14px;
          font-size: 11px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s;
        }

        .cd-status-active {
          background: #DCFCE7;
          color: #15803D;
        }

        .cd-status-inactive {
          background: #F1F5F9;
          color: #64748B;
        }

        .cd-actions-cell {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .cd-icon-btn {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          border: 1px solid transparent;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
          background: #F1F5F9;
        }

        .cd-edit-btn {
          color: #0284C7;
        }

        .cd-edit-btn:hover {
          background: #E0F2FE;
          border-color: #7DD3FC;
        }

        .cd-delete-btn {
          color: #EF4444;
        }

        .cd-delete-btn:hover {
          background: #FEE2E2;
          border-color: #FCA5A5;
        }

        /* BUTTONS */
        .cd-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 9px 18px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s;
          border: none;
        }

        .cd-btn-primary {
          background: #0284C7;
          color: #FFFFFF;
          box-shadow: 0 2px 8px rgba(2, 132, 199, 0.25);
        }

        .cd-btn-primary:hover {
          background: #0369A1;
        }

        .cd-btn-secondary {
          background: #F8FAFC;
          color: #334155;
          border: 1px solid #CBD5E1;
        }

        .cd-btn-secondary:hover {
          background: #E2E8F0;
        }

        .cd-btn-danger-outline {
          background: #FEF2F2;
          color: #DC2626;
          border: 1px solid #FECACA;
        }

        .cd-btn-danger-outline:hover {
          background: #FEE2E2;
        }

        .cd-btn-sm {
          padding: 7px 14px;
          font-size: 12px;
        }

        /* MODAL */
        .cd-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(15, 23, 42, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .cd-modal-card {
          background: #FFFFFF;
          border-radius: 16px;
          width: 100%;
          max-width: 480px;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
          overflow: hidden;
          animation: modalPop 0.2s ease-out;
        }

        @keyframes modalPop {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }

        .cd-modal-header {
          padding: 20px 24px;
          background: #F8FAFC;
          border-bottom: 1px solid #E2E8F0;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
        }

        .cd-modal-header-text h3 {
          margin: 0 0 2px 0;
          font-size: 17px;
          font-weight: 800;
          color: #0F172A;
        }

        .cd-modal-header-text p {
          margin: 0;
          font-size: 12px;
          color: #64748B;
        }

        .cd-modal-close-btn {
          background: transparent;
          border: none;
          color: #64748B;
          cursor: pointer;
          padding: 4px;
          border-radius: 6px;
        }

        .cd-modal-close-btn:hover {
          background: #E2E8F0;
          color: #0F172A;
        }

        .cd-modal-body {
          padding: 24px;
        }

        .cd-form-group {
          margin-bottom: 18px;
        }

        .cd-form-label {
          display: block;
          font-size: 12.5px;
          font-weight: 700;
          color: #334155;
          margin-bottom: 6px;
        }

        .cd-req {
          color: #EF4444;
        }

        .cd-form-input {
          width: 100%;
          padding: 10px 14px;
          border-radius: 8px;
          border: 1px solid #CBD5E1;
          font-size: 13.5px;
          color: #0F172A;
          outline: none;
          box-sizing: border-box;
          transition: border-color 0.2s;
        }

        .cd-form-input:focus {
          border-color: #0284C7;
          box-shadow: 0 0 0 3px rgba(2, 132, 199, 0.15);
        }

        .cd-form-row {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .cd-checkbox-group {
          display: flex;
          align-items: center;
          padding-top: 18px;
        }

        .cd-checkbox-label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          font-weight: 600;
          color: #334155;
          cursor: pointer;
        }

        .cd-modal-footer {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 10px;
          margin-top: 24px;
          padding-top: 18px;
          border-top: 1px solid #F1F5F9;
        }

        /* EMPTY & LOADING */
        .cd-loading-state, .cd-empty-state {
          padding: 60px 20px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: #64748B;
          gap: 10px;
        }

        .cd-empty-state h4 {
          margin: 0;
          font-size: 16px;
          color: #1E293B;
        }

        .cd-empty-state p {
          margin: 0 0 8px 0;
          font-size: 13px;
        }

        .cd-spinner {
          width: 32px;
          height: 32px;
          border: 3px solid #E2E8F0;
          border-top-color: #0284C7;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        .spin {
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* TOAST */
        .cd-toast {
          position: fixed;
          bottom: 24px;
          right: 24px;
          background: #0F172A;
          color: #FFFFFF;
          padding: 12px 20px;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 10px;
          box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.3);
          z-index: 2000;
          animation: slideIn 0.3s ease;
        }

        .cd-toast-success {
          border-left: 4px solid #22C55E;
        }

        .cd-toast-error {
          border-left: 4px solid #EF4444;
        }

        @keyframes slideIn {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
